import { defineStore } from "pinia";
import { useRest } from "@/composables/useRest";
import { useToast } from "@/composables/useToast";
import { ref } from "vue";

export const useEvidenciaStore = defineStore("evidencia", () => {
  const rest = useRest();
  const toast = useToast();

  const evidenciasList = ref([]);
  const isLoading = ref(false);
  const isSubmitting = ref(false);
  const actuacionID = ref("");
  const formType = ref("view");
  const baseUrl = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/actuacion-evidencia`;
  const shouldOpenEditModal = ref(false);
  const shouldOpenDeleteModal = ref(false);
  const shouldOpenCreateModal = ref(false);
  const currentEvidenciaUrl = ref("");
  const modalAttributes = ref({
    title: "",
    message: "",
    modalType: "",
    showSecondaryButton: true,
    buttonLabels: [],
    onConfirm: () => {},
    onCancel: () => {},
  });

  const fetchEvidencias = async (id) => {
    actuacionID.value = id;
    try {
      isLoading.value = true;
      const response = await rest.get({
        url: `${baseUrl}/?search={"ActuacionID": [{"operator": "=", "value": "${actuacionID.value}"}]}`,
      });
      evidenciasList.value = response.data.data || [];
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar evidencias");
      evidenciasList.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const addEvidence = async (url) => {
    if (isSubmitting.value) return false;
    isSubmitting.value = true;

    try {
      const formData = {
        ActuacionID: actuacionID.value,
        Url: url,
      };
      const optionsPost = {
        url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/actuacion-evidencia`,
        formData: formData,
        headers: { "Content-Type": "multipart/form-data" },
      };
      await rest.post(optionsPost);
      await fetchEvidencias(actuacionID.value);
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Error al añadir evidencia");
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  const editEvidence = async (evidenciaID, url) => {
    if (isSubmitting.value) return false;
    isSubmitting.value = true;

    try {
      await rest.put({
        url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/actuacion-evidencia/${evidenciaID}`,
        formData: {
          Url: url,
          ActuacionID: actuacionID.value,
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchEvidencias(actuacionID.value);
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Error al editar evidencia");
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  const deleteEvidence = async (evidenciaID) => {
    if (isSubmitting.value) return false;
    isSubmitting.value = true;

    try {
      await rest.del({
        url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/actuacion-evidencia/${evidenciaID}`,
      });
      await fetchEvidencias(actuacionID.value);
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Error al eliminar evidencia");
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  const startEdit = (url = "") => {
    formType.value = "edit";
    currentEvidenciaUrl.value = url;
    shouldOpenEditModal.value = true;
    modalAttributes.value = {
      title: "Editar evidencia",
      message: "¿Está seguro de querer editar esta evidencia?",
      modalType: "warning",
      showSecondaryButton: true,
      buttonLabels: ["Editar", "Cancelar"],
      onConfirm: () => {},
      onCancel: () => {},
    };
  };

  const closeEditModal = () => {
    shouldOpenEditModal.value = false;
    currentEvidenciaUrl.value = "";
  };

  const startDelete = () => {
    formType.value = "delete";
    shouldOpenDeleteModal.value = true;
  };

  const closeDeleteModal = () => {
    shouldOpenDeleteModal.value = false;
  };

  const startCreate = () => {
    formType.value = "create";
    currentEvidenciaUrl.value = "";
    shouldOpenCreateModal.value = true;
  };

  const closeCreateModal = () => {
    shouldOpenCreateModal.value = false;
    currentEvidenciaUrl.value = "";
  };

  const setActuacionID = (id) => {
    actuacionID.value = id;
  };

  const resetStore = () => {
    evidenciasList.value = [];
    isLoading.value = false;
    isSubmitting.value = false;
    actuacionID.value = "";
  };

  return {
    // State
    evidenciasList,
    isLoading,
    isSubmitting,
    actuacionID,
    shouldOpenEditModal,
    shouldOpenDeleteModal,
    shouldOpenCreateModal,
    currentEvidenciaUrl,
    formType,

    // Actions
    fetchEvidencias,
    addEvidence,
    editEvidence,
    deleteEvidence,
    setActuacionID,
    resetStore,
    startEdit,
    closeEditModal,
    startDelete,
    closeDeleteModal,
    startCreate,
    closeCreateModal,
  };
});
